import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import FundTransfer, { AccountOption } from './FundTransfer';
import { FUND_TRANSFER_MARKETS, FundTransferMarket } from '../services/fundTransferContracts';
import { transferMarketFunds } from '../services/marketFundTransfer';
import {
  AccountBalance,
  ExchangeClientTrade,
  FuturesPosition,
  SpotBalance,
  closeExchangeClientPosition,
  fetchExchangeClientTrades,
} from '../services/exchangeClientTrades';

type TradesState = {
  loading: boolean;
  error: string | null;
  warnings: string[];
  trades: ExchangeClientTrade[];
  spotBalances: SpotBalance[];
  futuresPositions: FuturesPosition[];
  futuresBalances: AccountBalance | null;
  fundBalance: AccountBalance | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractErrorMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;

  if (!isRecord(value)) return null;

  const errValue = value.err;
  if (typeof errValue === 'string' && errValue.trim()) return errValue;

  if (isRecord(errValue)) {
    const candidateKeys = ['message', 'msg', 'error'];
    for (const key of candidateKeys) {
      const candidate = errValue[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
  }

  const candidateKeys = ['message', 'msg', 'error'];
  for (const key of candidateKeys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return null;
}

function normalizeArrayField<T>(value: unknown, fieldName: string, warnings: string[]): T[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value as T[];

  if (isRecord(value)) {
    const errorMessage = extractErrorMessage(value);
    if (errorMessage) {
      warnings.push(`${fieldName}: ${errorMessage}`);
      console.error(`[ExchangeClientTrades] ${fieldName} backend error`, {
        fieldName,
        receivedValue: value,
        errorMessage,
      });
      return [];
    }

    const nestedArrayKeys = ['data', 'items', 'balances', 'positions', 'rows'];
    for (const key of nestedArrayKeys) {
      const nestedValue = value[key];
      if (Array.isArray(nestedValue)) {
        warnings.push(`${fieldName} arrived under "${key}" instead of as an array.`);
        console.error(`[ExchangeClientTrades] ${fieldName} shape mismatch`, {
          fieldName,
          receivedType: typeof value,
          receivedValue: value,
        });
        return nestedValue as T[];
      }
    }
  }

  warnings.push(`${fieldName} has an invalid format and could not be displayed.`);
  console.error(`[ExchangeClientTrades] ${fieldName} shape mismatch`, {
    fieldName,
    receivedType: Array.isArray(value) ? 'array' : typeof value,
    receivedValue: value,
  });
  return [];
}

function normalizeBalanceField(
  value: unknown,
  fieldName: string,
  warnings: string[]
): AccountBalance | null {
  if (value == null) return null;
  if (isRecord(value)) {
    const errorMessage = extractErrorMessage(value);
    if (errorMessage) {
      warnings.push(`${fieldName}: ${errorMessage}`);
    }
    return value as AccountBalance;
  }

  warnings.push(`${fieldName} has an invalid format and could not be displayed.`);
  console.error(`[ExchangeClientTrades] ${fieldName} shape mismatch`, {
    fieldName,
    receivedType: Array.isArray(value) ? 'array' : typeof value,
    receivedValue: value,
  });
  return null;
}

export default function ExchangeClientTrades() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { exchangeClientId, profileId } = useParams();
  const location = useLocation();
  const accountState = location.state as
    | {
        accountName?: string;
        provider?: string;
      }
    | undefined;
  const accountTitle = accountState?.accountName ?? accountState?.provider ?? 'Exchange';
  const [state, setState] = useState<TradesState>({
    loading: true,
    error: null,
    warnings: [],
    trades: [],
    spotBalances: [],
    futuresPositions: [],
    futuresBalances: null,
    fundBalance: null,
  });
  const [closing, setClosing] = useState<{
    spot: Record<string, boolean>;
    futures: Record<string, boolean>;
  }>({ spot: {}, futures: {} });

  const load = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchExchangeClientTrades(token, exchangeClientId, profileId);
      console.log('[ExchangeClientTrades] Raw API response', {
        exchangeClientId,
        profileId,
        response: data,
      });
      if (!data.success) {
        throw new Error(data.message ?? 'Failed to load exchange client trades.');
      }

      const warnings: string[] = [];
      const trades = normalizeArrayField<ExchangeClientTrade>(data.trades, 'trades', warnings);
      const spotBalances = normalizeArrayField<SpotBalance>(
        data.exchangeData?.spotBalances,
        'spotBalances',
        warnings
      );
      const futuresPositions = normalizeArrayField<FuturesPosition>(
        data.exchangeData?.futuresPositions,
        'futuresPositions',
        warnings
      );
      const futuresBalances = normalizeBalanceField(
        data.exchangeData?.futuresBalances,
        'futuresBalances',
        warnings
      );
      const fundBalance = normalizeBalanceField(
        data.exchangeData?.fundBalance,
        'fundBalance',
        warnings
      );

      if (warnings.length > 0) {
        console.error('[ExchangeClientTrades] Response normalization warnings', {
          exchangeClientId,
          profileId,
          warnings,
          exchangeData: data.exchangeData,
        });
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        warnings,
        trades,
        spotBalances,
        futuresPositions,
        futuresBalances,
        fundBalance,
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load exchange client trades.';
      setState((prev) => ({ ...prev, loading: false, error: msg, warnings: [] }));
    }
  };

  useEffect(() => {
    void load();
  }, [token, exchangeClientId, profileId]);

  console.log('ExchangeClientTrades:', state.trades);

  const usdtSpotBalance = state.spotBalances.find((balance) => balance.asset === 'USDT');

  const getMarketLabel = (exchangeClient?: string) => {
    const normalized = (exchangeClient ?? '').toLowerCase();
    if (normalized.includes('futures')) return 'FUTURES';
    if (normalized.includes('spot')) return 'SPOT';
    return '—';
  };
  const MARKET_ACCOUNTS: AccountOption<FundTransferMarket>[] = FUND_TRANSFER_MARKETS.map((market) => ({
    id: market,
    name: market.charAt(0).toUpperCase() + market.slice(1),
  }));

  const futuresColumns = ['symbol', 'currency', 'positionAmt', 'availableAmt', 'positionSide'];
  const renderCellValue = (value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const closeSpotPosition = async (balance: SpotBalance, key: string) => {
    if (!exchangeClientId || !profileId) return;
    setClosing((prev) => ({ ...prev, spot: { ...prev.spot, [key]: true } }));
    try {
      await closeExchangeClientPosition(token, {
        exchangeClientId,
        profileId,
        market: 'spot',
        symbol: balance.asset,
        balance: balance.free,
      });
      void load();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to close spot position. Please try again.';
      setState((prev) => ({ ...prev, error: msg }));
    } finally {
      setClosing((prev) => ({ ...prev, spot: { ...prev.spot, [key]: false } }));
    }
  };

  const closeFuturesPosition = async (position: FuturesPosition, key: string) => {
    if (!exchangeClientId || !profileId) return;
    setClosing((prev) => ({ ...prev, futures: { ...prev.futures, [key]: true } }));
    try {
      await closeExchangeClientPosition(token, {
        exchangeClientId,
        profileId,
        market: 'futures',
        symbol: position.symbol ? String(position.symbol) : undefined,
      });
      void load();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to close futures position. Please try again.';
      setState((prev) => ({ ...prev, error: msg }));
    } finally {
      setClosing((prev) => ({ ...prev, futures: { ...prev.futures, [key]: false } }));
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        p: 2,
        pt: 4,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1100, width: '100%', borderRadius: '15px' }}>
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 4,
                py: 0.25,
                minWidth: 140,
                color: 'grey.800',
                borderColor: 'grey.300',
                bgcolor: 'grey.100',
                boxShadow: '0 6px 14px rgba(17, 24, 39, 0.08)',
                '&:hover': {
                  bgcolor: 'grey.200',
                  borderColor: 'grey.400',
                },
              }}
            >
              BACK
            </Button>
          </Box>

          <Stack direction="row" justifyContent="center">
            <Tooltip title="Exchange account balances, positions, and trades" arrow>
              <Chip
                label={accountTitle}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  fontSize: 16,
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 2,
                  boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                }}
              />
            </Tooltip>
          </Stack>

          {state.loading ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading trades...
              </Typography>
            </Stack>
          ) : null}

          {state.error ? <Alert severity="error">{state.error}</Alert> : null}
          {state.warnings.length > 0 ? (
            <Alert severity="warning">
              {state.warnings.map((warning, index) => (
                <Typography
                  key={`${warning}-${index}`}
                  component="div"
                  variant="body2"
                >
                  {`- ${warning}`}
                </Typography>
              ))}
            </Alert>
          ) : null}

          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="left" sx={{ mt: 3 }}>
              <Tooltip title="Base asset balances in USDT across all accounts" arrow>
                <Chip
                  label="Base Assets free Balance"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    color: 'grey.700',
                    borderRadius: '15px',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    px: 1,
                    lineHeight: 1,
                    boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                    background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                  }}
                />
              </Tooltip>
            </Stack>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '15px',
                borderTopLeftRadius: 0,
                overflow: 'hidden',
                borderColor: 'grey.200',
                boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
              }}
            >
              <Table size="small" aria-label="base assets balance">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'grey.200',
                      background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    }}
                  >
                    {['Spot (USDT)', 'Futures (USDT)', 'Fund (USDT)'].map((col) => (
                      <TableCell
                        key={col}
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                  <TableBody>
                  <TableRow sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                      {(parseFloat(usdtSpotBalance?.free ?? '0') || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                      {(parseFloat(state.futuresBalances?.free ?? '0') || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                      {(parseFloat(state.fundBalance?.free ?? '0') || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <FundTransfer
            title="MOVE FUNDS"
            accounts={MARKET_ACCOUNTS}
            token={token}
            onTransfer={(tk, fromId, toId, amount) =>
              transferMarketFunds(tk, {
                profileId: profileId!,
                exchangeClientId: exchangeClientId!,
                fromMarket: fromId,
                toMarket: toId,
                amount,
              })
            }
            onSuccess={() => void load()}
          />

          <Box sx={{ mb: 6 }}>
            <Stack direction="row" justifyContent="left" sx={{ mt: 3 }}>
              <Tooltip title="Spot account balances" arrow>
                <Chip
                  label="Spot Balances"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    color: 'grey.700',
                    borderRadius: '15px',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    px: 1,
                    lineHeight: 1,
                    boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                    background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)'
                  }}
                />
              </Tooltip>
            </Stack>

            {!state.loading && state.spotBalances.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: '15px',
                  borderTopLeftRadius: 0,
                  overflow: 'hidden',
                  overflowX: 'auto',
                  borderColor: 'grey.200',
                  boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
                }}
              >
                <Table size="small" aria-label="spot balances">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'grey.200',
                        background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Asset
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Display Name
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Free
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Locked
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Sell coins
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.spotBalances.map((balance, index) => {
                      const spotKey = `${balance.asset ?? 'asset'}-${index}`;
                      const isClosing = Boolean(closing.spot[spotKey]);
                      return (
                        <TableRow
                          key={spotKey}
                          sx={{
                            transition: 'background-color 150ms ease, transform 150ms ease',
                            '&:hover': { bgcolor: 'grey.50' },
                          }}
                        >
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            {balance.asset ?? '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            {balance.disPlayName ?? '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            {balance.free ?? '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            {balance.locked ?? '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            {balance.asset !== 'USDT' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isClosing}
                                startIcon={isClosing ? <CircularProgress size={14} /> : undefined}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '15px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: 'error.main',
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.dark',
                                    color: 'error.dark',
                                    bgcolor: 'error.light',
                                  },
                                }}
                                onClick={() => closeSpotPosition(balance, spotKey)}
                              >
                                Sell coins
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            {!state.loading && state.spotBalances.length === 0 && !state.error ? (
              <Alert severity="info">No spot balances found.</Alert>
            ) : null}
          </Box>

          <Box sx={{ mb: 6 }}>
            <Stack direction="row" justifyContent="left" sx={{ mt: 3 }}>
              <Tooltip title="Futures account Positions" arrow>
                <Chip
                  label="Futures Positions"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    color: 'grey.700',
                    borderRadius: '15px',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    px: 1,
                    lineHeight: 1,
                    boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                  }}
                />
              </Tooltip>
            </Stack>

            {!state.loading && state.futuresPositions.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: '15px',
                  borderTopLeftRadius: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  borderColor: 'grey.200',
                  boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
                }}
              >
                <Table size="small" aria-label="futures positions" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'grey.200',
                        background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                      }}
                    >
                      {futuresColumns.map((column) => (
                        <TableCell
                          key={column}
                          align="center"
                          sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                        >
                          {column}
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Close position
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.futuresPositions.map((position, index) => {
                      const futuresKey = `futures-position-${index}`;
                      const isClosing = Boolean(closing.futures[futuresKey]);
                      return (
                        <TableRow
                          key={futuresKey}
                          sx={{
                            transition: 'background-color 150ms ease, transform 150ms ease',
                            '&:hover': { bgcolor: 'grey.50' },
                          }}
                        >
                          {futuresColumns.map((column) => (
                            <TableCell
                              key={`${column}-${index}`}
                              align="center"
                              sx={{ fontSize: 13, color: 'grey.900' }}
                            >
                              {renderCellValue(position[column])}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontSize: 13, color: 'grey.900' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isClosing}
                              startIcon={isClosing ? <CircularProgress size={14} /> : undefined}
                              sx={{
                                textTransform: 'none',
                                borderRadius: '15px',
                                fontSize: 12,
                                fontWeight: 600,
                                color: 'error.main',
                                borderColor: 'error.main',
                                '&:hover': {
                                  borderColor: 'error.dark',
                                  color: 'error.dark',
                                  bgcolor: 'error.light',
                                },
                              }}
                              onClick={() => closeFuturesPosition(position, futuresKey)}
                            >
                              Close position
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            {!state.loading && state.futuresPositions.length === 0 && !state.error ? (
              <Alert severity="info">No futures positions found.</Alert>
            ) : null}
          </Box>

          <Box sx={{ mb: 6 }}>
            <Stack direction="row" justifyContent="left" sx={{ mt: 3 }}>
              <Tooltip title="Exchange account Trades" arrow>
                <Chip
                  label="Exchange Account Trades"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    color: 'grey.700',
                    borderRadius: '15px',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    px: 1,
                    lineHeight: 1,
                    boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                  }}
                />
              </Tooltip>
            </Stack>

            {!state.loading && state.trades.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: '15px',
                  borderTopLeftRadius: 0,
                  overflow: 'hidden',
                  borderColor: 'grey.200',
                  boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
                }}
              >
                <Table size="small" aria-label="exchange client trades">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'grey.200',
                        background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Symbol
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Market
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.trades.map((trade, index) => (
                      <TableRow
                        key={trade._id ?? trade.id ?? index}
                        hover
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/trade/${trade._id ?? trade.id ?? index}`, { state: { trade } })}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/trade/${trade._id ?? trade.id ?? index}`, { state: { trade } });
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 150ms ease, transform 150ms ease',
                          '&:hover': { bgcolor: 'grey.50' },
                        }}
                      >
                        <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                          {trade.symbol ?? '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                          {getMarketLabel(trade.exchange_client)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            {!state.loading && state.trades.length === 0 && !state.error ? (
              <Alert severity="info">No trades found.</Alert>
            ) : null}
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
